import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Calendar as CalendarIcon,
  List,
  Plus,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Lock,
  PowerOff,
  UserX,
} from "lucide-react";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sportscanner/ui";
import { apiGet } from "@/lib/api";
import {
  useMe,
  useSubscriptionActive,
  type MeClub,
  type MeSport,
} from "@/lib/me";
import ClubScopeShell from "@/components/club-scope-shell";
import ShareClubButton from "@/components/share-club-button";
import WeeklyCalendar, {
  type CalendarBooking,
} from "@/components/booking/weekly-calendar";
import BlockSlotDialog from "@/components/booking/block-slot-dialog";
import BlockRemoveDialog, {
  type BlockDetail,
} from "@/components/booking/block-remove-dialog";
import ClubClosureDialog from "@/components/booking/club-closure-dialog";
import ClosureRemoveDialog, {
  type ClosureDetail,
} from "@/components/booking/closure-remove-dialog";
import {
  deriveLayout,
  resolveLayoutSelection,
  type Layout,
} from "@/components/booking/derive-layout";
import ScopeChooser from "@/components/booking/scope-chooser";
import BookingDialog from "@/components/booking/booking-dialog";
import BookingDetailsDialog from "@/components/booking/booking-details-dialog";
import {
  addDays,
  dateStrToEntry,
  startOfWeekMonday,
  toLocalDateStr,
} from "@/components/booking/helpers";

type ViewMode = "calendar" | "list";
type RangePreset = "thisWeek" | "nextWeek" | "allTime" | "custom";

const PAGE_SIZE = 25;
const ALL_COURTS = "__all__";

interface BookingsResponse {
  startDate: string | null;
  endDate: string | null;
  allTime: boolean;
  page: number;
  pageSize: number | null;
  total: number;
  totalPages: number;
  bookings: Array<
    CalendarBooking & {
      playerPhone: string;
      playerEmail: string | null;
      sessionDurationMinutes: number;
      pricePerSession: string;
      noShow: boolean;
      createdAt: string;
    }
  >;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export default function BookingsPage() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto">
      <ClubScopeShell>
        {(clubId) => <BookingsPageContent clubId={clubId} />}
      </ClubScopeShell>
    </div>
  );
}

function BookingsPageContent({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const { user, club } = useMe();
  const subscriptionActive = useSubscriptionActive();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setView] = useState<ViewMode>("calendar");
  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week, 1 = next, …
  const [allTime, setAllTime] = useState(false);

  // List-view filters
  const [filterCourtId, setFilterCourtId] = useState<string>(ALL_COURTS);
  const [filterPlayerName, setFilterPlayerName] = useState("");
  const [filterPlayerPhone, setFilterPlayerPhone] = useState("");
  const [filterNoShow, setFilterNoShow] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedName = useDebouncedValue(filterPlayerName, 300);
  const debouncedPhone = useDebouncedValue(filterPlayerPhone, 300);

  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingShortcut, setBookingShortcut] = useState<
    { date: string; time: string; sportId?: string } | undefined
  >(undefined);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockRemove, setBlockRemove] = useState<BlockDetail | null>(null);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureRemove, setClosureRemove] = useState<ClosureDetail | null>(
    null,
  );

  // Open booking dialog on ?action=new query param
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setBookingShortcut(undefined);
      setBookingDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
    const bookingId = searchParams.get("bookingId");
    if (bookingId) {
      setDetailsId(bookingId);
      const next = new URLSearchParams(searchParams);
      next.delete("bookingId");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 1 when any list filter changes
  useEffect(() => {
    setPage(1);
  }, [
    filterCourtId,
    debouncedName,
    debouncedPhone,
    filterNoShow,
    weekOffset,
    allTime,
    view,
  ]);

  // Going back to calendar from a list-only mode (allTime) should restore a week view
  useEffect(() => {
    if (view === "calendar" && allTime) {
      setAllTime(false);
      setWeekOffset(0);
    }
  }, [view, allTime]);

  const weekStart = useMemo(
    () => addDays(startOfWeekMonday(new Date()), weekOffset * 7),
    [weekOffset],
  );
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const range = useMemo(
    () => ({ start: toLocalDateStr(weekStart), end: toLocalDateStr(weekEnd) }),
    [weekStart, weekEnd],
  );

  const rangePreset: RangePreset = allTime
    ? "allTime"
    : weekOffset === 0
      ? "thisWeek"
      : weekOffset === 1
        ? "nextWeek"
        : "custom";

  const [clubData, setClubData] = useState<MeClub | null>(club);
  useEffect(() => {
    setClubData(club);
  }, [club]);

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
    enabled: !club || club.id !== clubId,
  });
  useEffect(() => {
    if (clubQuery.data) setClubData(clubQuery.data);
  }, [clubQuery.data]);

  // Build query string for the current view
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (allTime) {
      params.set("allTime", "true");
    } else {
      params.set("startDate", range.start);
      params.set("endDate", range.end);
    }
    if (view === "list") {
      // List view shows cancelled bookings too; the calendar stays bookings-only.
      params.set("includeCancelled", "true");
      if (filterCourtId !== ALL_COURTS) params.set("courtId", filterCourtId);
      if (debouncedName.trim()) params.set("playerName", debouncedName.trim());
      if (debouncedPhone.trim())
        params.set("playerPhone", debouncedPhone.trim());
      if (filterNoShow) params.set("noShow", "true");
      params.set("page", String(page));
      params.set("pageSize", String(PAGE_SIZE));
    }
    return params.toString();
  }, [
    allTime,
    range.start,
    range.end,
    view,
    filterCourtId,
    debouncedName,
    debouncedPhone,
    filterNoShow,
    page,
  ]);

  const bookingsQuery = useQuery({
    queryKey: ["admin", "bookings", clubId, queryString],
    queryFn: () =>
      apiGet<BookingsResponse>(
        `/api/admin/clubs/${clubId}/bookings?${queryString}`,
      ),
    placeholderData: keepPreviousData,
    // Bookings change frequently and are the primary work surface; make sure
    // every navigation to /bookings shows fresh data instead of cached results.
    // The page is often left open for long stretches, so also poll every 5 min
    // so the view doesn't silently go stale while it sits in the foreground.
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 5 * 60_000,
  });

  // Blocks for the visible week. Skipped when the list view is active since
  // list view only shows bookings, never blocks.
  const blocksQuery = useQuery({
    queryKey: [
      "admin",
      "blocks",
      clubId,
      view === "calendar" && !allTime ? range.start : null,
      view === "calendar" && !allTime ? range.end : null,
    ],
    queryFn: () =>
      apiGet<{ blocks: BlockDetail[] }>(
        `/api/admin/clubs/${clubId}/blocks?startDate=${range.start}&endDate=${range.end}`,
      ),
    enabled: view === "calendar" && !allTime,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 5 * 60_000,
  });

  // Whole-club closures for the visible week. Same gating as blocks.
  const closuresQuery = useQuery({
    queryKey: [
      "admin",
      "closures",
      clubId,
      view === "calendar" && !allTime ? range.start : null,
      view === "calendar" && !allTime ? range.end : null,
    ],
    queryFn: () =>
      apiGet<{ closures: ClosureDetail[] }>(
        `/api/admin/clubs/${clubId}/closures?startDate=${range.start}&endDate=${range.end}`,
      ),
    enabled: view === "calendar" && !allTime,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 5 * 60_000,
  });

  const role = user?.role;
  const canCancel =
    role === "MANAGER" ||
    role === "SUPERADMIN" ||
    Boolean(
      role === "STAFF" &&
      (user as unknown as { permissions?: { cancel_booking?: boolean } })
        ?.permissions?.cancel_booking,
    );
  const canEdit =
    role === "MANAGER" ||
    role === "SUPERADMIN" ||
    Boolean(
      role === "STAFF" &&
      (user as unknown as { permissions?: { edit_booking?: boolean } })
        ?.permissions?.edit_booking,
    );
  const canCreate =
    role === "MANAGER" ||
    role === "SUPERADMIN" ||
    Boolean(
      role === "STAFF" &&
      (user as unknown as { permissions?: { create_booking?: boolean } })
        ?.permissions?.create_booking,
    );
  const canManageBlocks =
    role === "MANAGER" ||
    role === "SUPERADMIN" ||
    Boolean(
      role === "STAFF" &&
      (user as unknown as { permissions?: { manage_blocks?: boolean } })
        ?.permissions?.manage_blocks,
    );
  const canManageClosures =
    role === "MANAGER" ||
    role === "SUPERADMIN" ||
    Boolean(
      role === "STAFF" &&
      (user as unknown as { permissions?: { manage_closures?: boolean } })
        ?.permissions?.manage_closures,
    );

  const todayStr = toLocalDateStr(new Date());

  function refreshAll() {
    void bookingsQuery.refetch();
    void blocksQuery.refetch();
    void closuresQuery.refetch();
  }

  function handleBlockClick(blockId: string) {
    const b = blocksQuery.data?.blocks.find((x) => x.id === blockId);
    if (!b) return;
    setBlockRemove(b);
  }

  function handleClosureClick(date: string) {
    const c = closuresQuery.data?.closures.find((x) => x.date === date);
    if (!c) return;
    setClosureRemove(c);
  }

  function handleCellAdd(date: string, time: string, sportId?: string) {
    setBookingShortcut({ date, time, sportId });
    setBookingDialogOpen(true);
  }

  function handleNewBookingClick() {
    setBookingShortcut(undefined);
    setBookingDialogOpen(true);
  }

  // Label shown inside the filter dropdown trigger
  const filterTriggerLabel = useMemo(() => {
    if (allTime) return t("bookings.filters.allTime");
    if (weekOffset === 0) return t("bookings.filters.thisWeek");
    if (weekOffset === 1) return t("bookings.filters.nextWeek");
    const startEntry = dateStrToEntry(range.start);
    const endEntry = dateStrToEntry(range.end);
    return `${startEntry.date}. ${t(`months.${startEntry.monthKey}`).slice(0, 3)} – ${endEntry.date}. ${t(`months.${endEntry.monthKey}`).slice(0, 3)}`;
  }, [allTime, weekOffset, range.start, range.end, t]);

  function handleFilterChange(value: string) {
    if (value === "thisWeek") {
      setAllTime(false);
      setWeekOffset(0);
    } else if (value === "nextWeek") {
      setAllTime(false);
      setWeekOffset(1);
    } else if (value === "allTime") {
      setAllTime(true);
    }
  }

  function clearListFilters() {
    setFilterCourtId(ALL_COURTS);
    setFilterPlayerName("");
    setFilterPlayerPhone("");
    setFilterNoShow(false);
  }

  const sports: MeSport[] = clubData?.sports ?? [];
  const courts = clubData?.courts ?? [];

  const layout = useMemo<Layout | null>(
    () =>
      clubData
        ? deriveLayout({
            sports: clubData.sports,
            courts: clubData.courts,
            clubWorkingHoursStart: clubData.workingHoursStart,
            clubWorkingHoursEnd: clubData.workingHoursEnd,
          })
        : null,
    [clubData],
  );

  const selection = useMemo(
    () => (layout ? resolveLayoutSelection(layout, searchParams) : null),
    [layout, searchParams],
  );

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  const data = bookingsQuery.data;
  const bookings = data?.bookings ?? [];
  const isInitialLoading = bookingsQuery.isPending && !data;

  const hasListFilters =
    filterCourtId !== ALL_COURTS ||
    filterPlayerName.trim().length > 0 ||
    filterPlayerPhone.trim().length > 0 ||
    filterNoShow;

  return (
    <>
      {/* Row 1: title + club-wide actions (copy URL, block, close club) */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
            {t("bookings.title")}
          </h1>
          <p className="text-sm text-neutral-500">{t("bookings.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <ShareClubButton clubId={clubId} />
          {canManageBlocks && (
            <Button
              onClick={() => setBlockDialogOpen(true)}
              disabled={!subscriptionActive}
              variant="outline"
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
            >
              <Lock className="w-4 h-4 mr-1.5" />
              {t("bookings.blockSlot")}
            </Button>
          )}
          {canManageClosures && (
            <Button
              onClick={() => setClosureDialogOpen(true)}
              disabled={!subscriptionActive}
              variant="outline"
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
            >
              <PowerOff className="w-4 h-4 mr-1.5" />
              {t("bookings.closeClub")}
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: scope chooser + view controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div className="min-w-0">
          {view === "calendar" && layout && selection && (
            <ScopeChooser
              layout={layout}
              selection={selection}
              setParam={setParam}
            />
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="inline-flex items-center bg-white border border-neutral-200 rounded-lg p-1">
            <button
              onClick={() => setView("calendar")}
              className={`px-2.5 py-1.5 rounded-md transition-colors cursor-pointer ${
                view === "calendar"
                  ? "bg-primary-600 text-white"
                  : "text-neutral-500 hover:text-neutral-700"
              }`}
              aria-label={t("bookings.view.calendar")}
            >
              <CalendarIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-2.5 py-1.5 rounded-md transition-colors cursor-pointer ${
                view === "list"
                  ? "bg-primary-600 text-white"
                  : "text-neutral-500 hover:text-neutral-700"
              }`}
              aria-label={t("bookings.view.list")}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Range filter */}
          <Select value={rangePreset} onValueChange={handleFilterChange}>
            <SelectTrigger className="bg-white border-neutral-200 sm:min-w-[180px]">
              <SelectValue>{filterTriggerLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="thisWeek">
                {t("bookings.filters.thisWeek")}
              </SelectItem>
              <SelectItem value="nextWeek">
                {t("bookings.filters.nextWeek")}
              </SelectItem>
              {view === "list" && (
                <SelectItem value="allTime">
                  {t("bookings.filters.allTime")}
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {/* Week pagination arrows */}
          {!allTime && (
            <div className="inline-flex items-center bg-white border border-neutral-200 rounded-lg">
              <button
                onClick={() => setWeekOffset((w) => w - 1)}
                className="p-2 hover:bg-neutral-50 rounded-l-lg cursor-pointer"
                aria-label="prev week"
              >
                <ChevronLeft className="w-4 h-4 text-neutral-500" />
              </button>
              <button
                onClick={() => setWeekOffset((w) => w + 1)}
                className="p-2 hover:bg-neutral-50 rounded-r-lg border-l border-neutral-200 cursor-pointer"
                aria-label="next week"
              >
                <ChevronRight className="w-4 h-4 text-neutral-500" />
              </button>
            </div>
          )}

          {canCreate && (
            <Button
              onClick={handleNewBookingClick}
              disabled={!subscriptionActive}
              className="rounded-full bg-primary-600 hover:bg-primary-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t("bookings.newBooking")}
            </Button>
          )}
        </div>
      </div>

      {/* List view filters */}
      {view === "list" && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-wrap">
          <div className="relative w-full sm:w-56">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={filterPlayerName}
              onChange={(e) => setFilterPlayerName(e.target.value)}
              placeholder={t("bookings.filters.playerNamePlaceholder")}
              className="pl-9 bg-white border-neutral-200 w-full"
            />
          </div>
          <div className="relative w-full sm:w-44">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={filterPlayerPhone}
              onChange={(e) => setFilterPlayerPhone(e.target.value)}
              placeholder={t("bookings.filters.playerPhonePlaceholder")}
              className="pl-9 bg-white border-neutral-200 w-full"
            />
          </div>
          <Select value={filterCourtId} onValueChange={setFilterCourtId}>
            <SelectTrigger className="bg-white border-neutral-200 w-full sm:w-auto sm:min-w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_COURTS}>
                {t("bookings.filters.allCourts")}
              </SelectItem>
              {courts.map((c) => {
                const sport = sports.find((s) => s.id === c.sportId);
                const label = sport
                  ? `${t(`sports.${sport.name}`)} · ${c.name}`
                  : c.name;
                return (
                  <SelectItem key={c.id} value={c.id}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => setFilterNoShow((v) => !v)}
            aria-pressed={filterNoShow}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors ${
              filterNoShow
                ? "bg-amber-50 border-amber-200 text-amber-700"
                : "bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50"
            }`}
          >
            <UserX className="w-4 h-4" />
            {t("bookings.filters.noShow")}
          </button>
          {hasListFilters && (
            <button
              onClick={clearListFilters}
              className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 cursor-pointer px-2 py-1"
            >
              <X className="w-4 h-4" />
              {t("bookings.filters.clear")}
            </button>
          )}
        </div>
      )}

      {!clubData ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </div>
      ) : view === "calendar" ? (
        isInitialLoading ? (
          <div className="bg-white rounded-xl border border-neutral-200 p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
          </div>
        ) : !layout || !selection ? (
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center text-sm text-neutral-500">
            {t("bookings.calendar.noActiveCourts")}
          </div>
        ) : (
          <div
            className={
              bookingsQuery.isFetching ? "opacity-80 transition-opacity" : ""
            }
          >
            <WeeklyCalendar
              weekStart={weekStart}
              bookings={bookings}
              blocks={blocksQuery.data?.blocks ?? []}
              closures={closuresQuery.data?.closures ?? []}
              courts={selection.scope.courts}
              sports={selection.scope.sports}
              sessionDurationMinutes={selection.scope.sessionDurationMinutes}
              workingHoursStart={selection.scope.workingHoursStart}
              workingHoursEnd={selection.scope.workingHoursEnd}
              todayStr={todayStr}
              sportFilter={selection.sportFilter}
              onCellAdd={(date, time) => {
                const scopeSport =
                  selection.scope.sports.length === 1
                    ? selection.scope.sports[0].id
                    : (selection.sportFilter ?? undefined);
                handleCellAdd(date, time, scopeSport);
              }}
              onBookingClick={(id) => setDetailsId(id)}
              onBlockClick={handleBlockClick}
              onClosureClick={handleClosureClick}
            />
          </div>
        )
      ) : (
        <BookingsList
          bookings={bookings}
          sports={sports}
          page={page}
          pageSize={PAGE_SIZE}
          total={data?.total ?? 0}
          totalPages={data?.totalPages ?? 1}
          isInitialLoading={isInitialLoading}
          isFetching={bookingsQuery.isFetching}
          onPageChange={setPage}
          onClick={(id) => setDetailsId(id)}
        />
      )}

      {bookingDialogOpen && (
        <BookingDialog
          open={bookingDialogOpen}
          onOpenChange={(o) => {
            setBookingDialogOpen(o);
            if (!o) setBookingShortcut(undefined);
            // Refetch the calendar in the background so it reflects the same
            // truth the dialog's availability query is about to fetch — a
            // booking made in another tab won't lag behind the dialog's view.
            if (o) refreshAll();
          }}
          clubId={clubId}
          isFree={clubData?.isFree ?? false}
          shortcut={bookingShortcut}
          onCreated={refreshAll}
        />
      )}

      <BookingDetailsDialog
        open={Boolean(detailsId)}
        onOpenChange={(o) => {
          if (!o) setDetailsId(null);
        }}
        clubId={clubId}
        isFree={clubData?.isFree ?? false}
        bookingId={detailsId}
        canCancel={canCancel}
        canEdit={canEdit}
        onCancelled={refreshAll}
        onRescheduled={refreshAll}
      />

      {blockDialogOpen && (
        <BlockSlotDialog
          open={blockDialogOpen}
          onOpenChange={setBlockDialogOpen}
          clubId={clubId}
          club={clubData}
          onCreated={refreshAll}
        />
      )}

      <BlockRemoveDialog
        open={Boolean(blockRemove)}
        onOpenChange={(o) => {
          if (!o) setBlockRemove(null);
        }}
        clubId={clubId}
        block={blockRemove}
        onRemoved={refreshAll}
      />

      {closureDialogOpen && (
        <ClubClosureDialog
          open={closureDialogOpen}
          onOpenChange={setClosureDialogOpen}
          clubId={clubId}
          onCreated={refreshAll}
        />
      )}

      <ClosureRemoveDialog
        open={Boolean(closureRemove)}
        onOpenChange={(o) => {
          if (!o) setClosureRemove(null);
        }}
        clubId={clubId}
        closure={closureRemove}
        onRemoved={refreshAll}
      />
    </>
  );
}

function BookingsList({
  bookings,
  sports,
  page,
  pageSize,
  total,
  totalPages,
  isInitialLoading,
  isFetching,
  onPageChange,
  onClick,
}: {
  bookings: BookingsResponse["bookings"];
  sports: MeSport[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  isInitialLoading: boolean;
  isFetching: boolean;
  onPageChange: (page: number) => void;
  onClick: (id: string) => void;
}) {
  const { t } = useTranslation();

  if (isInitialLoading) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 p-8 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className={`bg-white rounded-xl border border-neutral-200 overflow-hidden ${
          isFetching ? "opacity-80 transition-opacity" : ""
        }`}
      >
        {bookings.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-neutral-500">
              {t("bookings.table.empty")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <Th>{t("bookings.table.time")}</Th>
                  <Th>{t("bookings.table.court")}</Th>
                  <Th>{t("bookings.table.sport")}</Th>
                  <Th>{t("bookings.table.player")}</Th>
                  <Th>{t("bookings.table.phone")}</Th>
                  <Th>{t("bookings.table.status")}</Th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("bookings.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {bookings.map((b) => {
                  const sport = sports.find((s) => s.id === b.sportId);
                  return (
                    <tr
                      key={b.id}
                      className={`hover:bg-neutral-50 transition-colors ${
                        b.status === "CANCELLED" ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-neutral-800">
                        {b.date} · {b.startTime}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-700">
                        {b.courtName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-700">
                        {sport ? t(`sports.${sport.name}`) : b.sportName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-700">
                        {b.playerName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-700">
                        {b.playerPhone}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {b.status === "BOOKED" && b.noShow ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                            <UserX className="w-3 h-3" />
                            {t("bookings.details.noShowBadge")}
                          </span>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              b.status === "BOOKED"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {t(`bookings.status.${b.status}`)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => onClick(b.id)}
                          className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                        >
                          {t("bookings.table.view")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination — always show the count; show prev/next only when there's
          more than one page. */}
      {total > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-neutral-600">
          <span>{t("bookings.pagination.totalCount", { count: total })}</span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                {t("bookings.pagination.previous")}
              </button>
              <span className="px-2 text-sm text-neutral-500">
                {t("bookings.pagination.page", { page, total: totalPages })}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("bookings.pagination.next")}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
      {children}
    </th>
  );
}
